/**
 * 企业微信 JS-SDK 封装（官方 @wecom/jssdk）
 *
 * 文档：
 *  - 开始使用 https://developer.work.weixin.qq.com/document/path/90514
 *  - 自定义转发到会话 https://developer.work.weixin.qq.com/document/path/100505
 *
 * 使用前提：
 *  - 应用后台「可信域名」需包含当前页面域名（须 ICP 备案并通过域名归属验证），
 *    分享卡片的 link 也必须落在可信域名下；
 *  - 后端提供 GET /wework/jsapi/signature?url= 接口，分别用「企业 jsapi_ticket」与
 *    「应用 jsapi_ticket」（getticket&type=agent）计算两级 sha1 签名后返回。
 */
import {
  ensureAgentConfigReady,
  ensureCorpConfigReady,
  hideAllNonBaseMenuItem,
  hideMenuItems,
  register,
  shareAppMessage,
  type CommonResult,
  type RegisterOptions,
  type SignatureData,
} from "@wecom/jssdk";
import { getJsApiSignature, type JsApiSignature } from "@/api/login";
import { getAuthToken } from "@/utils/wechat";

// 企业 ID 与自建应用 AgentId：与网页授权共用 .env 配置
const CORP_ID = process.env.VUE_APP_WX_APPID || "";
const AGENT_ID = process.env.VUE_APP_WX_AGENTID || "";

/** 是否在企业微信客户端内打开（企业微信 UA 携带 wxwork 标识） */
export const isWxWorkEnv = (): boolean => /wxwork/i.test(navigator.userAgent);

/**
 * 签名用页面地址：JS-SDK 要求签名 url 与调用页面的地址一致，且不含 # 及其后内容。
 * hash 路由下 # 前的部分在页面存续期内不变，因此整个会话只需一个签名；
 * 这里同时剔除授权回跳残留的 query 参数，避免签名地址与实际校验地址不一致
 */
export const getSignatureUrl = (): string => {
  const { origin, pathname, search } = window.location;
  const params = new URLSearchParams(search);
  ["code", "state", "accessToken", "authMode"].forEach((key) =>
    params.delete(key)
  );
  const qs = params.toString();
  return `${origin}${pathname}${qs ? `?${qs}` : ""}`;
};

// 从后端签名数据中取指定身份的签名字段；应用签名字段缺省时复用企业签名，
// 降低联调期对后端字段完整性的依赖
const pickSignatureData = (
  data: JsApiSignature | undefined | null,
  level: "corp" | "agent"
): SignatureData => {
  const sig = data || ({} as JsApiSignature);
  const signature =
    level === "agent"
      ? sig.agentSignature || sig.signature || ""
      : sig.signature || "";
  if (!signature) {
    throw new Error("后端未返回有效的 JS-SDK 签名");
  }
  return {
    timestamp:
      (level === "agent" ? sig.agentTimestamp : sig.timestamp) ||
      Math.floor(Date.now() / 1000),
    nonceStr: (level === "agent" ? sig.agentNonceStr : sig.nonceStr) || "",
    signature,
  };
};

let readyPromise: Promise<void> | null = null;

/**
 * 注册 JS-SDK 身份并等待 config / agentConfig 完成。
 * 「自定义转发到会话」属于应用身份接口，需要两级注册全部成功。
 * 注册失败不缓存结果，下次调用自动重试；成功后整个页面生命周期内复用
 */
const ensureSdkReady = async (): Promise<void> => {
  if (!isWxWorkEnv()) {
    return Promise.reject(new Error("当前不在企业微信环境"));
  }
  if (!CORP_ID) {
    return Promise.reject(new Error("缺少 VUE_APP_WX_APPID 配置"));
  }
  if (readyPromise) return readyPromise;

  readyPromise = (async () => {
    // 签名接口依赖登录态（axios 自动附带 token），须在企业微信已授权登录后调用
    const res = await getJsApiSignature({ url: getSignatureUrl() });
    const data = res?.data;
    const options: RegisterOptions = {
      corpId: data?.corpId || CORP_ID,
      jsApiList: ["shareAppMessage", "hideMenuItems", "hideAllNonBaseMenuItem"],
      getConfigSignature: () => pickSignatureData(data, "corp"),
      getAgentConfigSignature: () => pickSignatureData(data, "agent"),
    };
    if (data?.agentId || AGENT_ID) {
      options.agentId = data?.agentId ?? AGENT_ID;
    }
    register(options);
    await ensureCorpConfigReady();
    await ensureAgentConfigReady();
  })();

  try {
    await readyPromise;
  } catch (error) {
    // 失败后清空缓存，允许用户再次点击时重试（如后端临时不可用）
    readyPromise = null;
    throw error;
  }
};

export interface WxShareCard {
  /** 分享标题 */
  title: string;
  /** 分享描述 */
  desc: string;
  /** 分享链接（必须为应用可信域名下的地址） */
  link: string;
  /** 分享卡片图标（须为可公网访问的绝对地址） */
  imgUrl: string;
}

export type ShareResult = "success" | "cancel";

/**
 * 调起「自定义转发到会话」：弹出会话选择器，用户选择会话后发送分享卡片
 * @returns "success" 已发送；"cancel" 用户取消转发
 * @throws 未在企业微信环境、注册/鉴权失败或调起失败时抛错，由调用方决定降级策略
 */
export const shareToChat = (card: WxShareCard): Promise<ShareResult> =>
  ensureSdkReady().then(
    () =>
      new Promise<ShareResult>((resolve, reject) => {
        const call = shareAppMessage({
          ...card,
          success: () => resolve("success"),
          cancel: () => resolve("cancel"),
          fail: (res: CommonResult) =>
            reject(new Error(res?.errMsg || "转发失败")),
        });
        // 结果已由上方回调消费，吞掉 SDK 返回值中重复的 reject，避免 unhandledrejection
        call.catch(() => undefined);
      })
  );

// ===== 右上角菜单隐藏（转发/分享、复制链接、浏览器打开等）=====

/**
 * 需要额外显式隐藏的菜单项：所有转发/分享类 + 复制链接 + 浏览器打开。
 * 主力是 hideAllNonBaseMenuItem（一次性隐藏全部非基础按钮），
 * 这份列表作为个别客户端不认 hideAllNonBaseMenuItem 时的双保险
 */
export const HIDDEN_MENU_ITEMS = [
  "menuItem:share:appMessage", // 转发给朋友/会话（含分享给微信好友）
  "menuItem:share:timeline", // 分享到朋友圈
  "menuItem:share:qq", // 分享到 QQ
  "menuItem:share:weiboApp", // 分享到微博
  "menuItem:share:email", // 发送邮件
  "menuItem:share:brand", // 分享到企业微信
  "menuItem:copyLink", // 复制链接
  "menuItem:openWithSafari", // 在 Safari 中打开
  "menuItem:openWithQQBrowser", // 用 QQ 浏览器打开
  "menuItem:originUrl", // 原网页/用默认浏览器打开
];

// 客户端内置 WeixinJSBridge 的最小类型（仅用到 call）
interface WxJsBridge {
  call(api: string, params?: Record<string, unknown>): void;
}

const getJsBridge = (): WxJsBridge | undefined =>
  (window as unknown as { WeixinJSBridge?: WxJsBridge }).WeixinJSBridge;

// 节流间隔：路由切换会反复触发隐藏，短时间内无需重复调起客户端接口
const HIDE_THROTTLE_INTERVAL = 2000;
let lastHideAt = 0;

/**
 * 通过客户端内置 WeixinJSBridge 隐藏菜单：无需 JS-SDK 签名，
 * 页面加载后即可立即生效，作为签名流程完成前后的兜底手段。
 * 先调 hideAllNonBaseMenuItem 隐藏全部非基础按钮，再用列表精确补一刀
 * @returns 桥是否可用（不可用时由调用方监听就绪事件补调）
 */
const hideMenusByBridge = (): boolean => {
  const bridge = getJsBridge();
  if (!bridge) return false;
  try {
    bridge.call("hideAllNonBaseMenuItem");
    bridge.call("hideMenuItems", { menuList: HIDDEN_MENU_ITEMS });
    lastHideAt = Date.now();
    return true;
  } catch (error) {
    console.warn("[wecomSdk] WeixinJSBridge 隐藏菜单失败：", error);
    return false;
  }
};

/**
 * 隐藏右上角「转发/分享给微信好友」「复制链接」「在 Safari 中打开」等
 * 所有非基础菜单项，全局生效、可重复调用。
 *
 * 双通道保证效果：
 *  1. WeixinJSBridge 即时隐藏 —— 无需签名，页面加载后立刻生效；
 *  2. @wecom/jssdk 官方接口 —— 签名 config 成功后再正式设置一次。
 *
 * 说明：
 *  - 仅影响右上角「...」菜单的展示，不影响 shareToChat 主动调起的转发；
 *  - 企业微信在部分场景（页面重新展示等）可能恢复默认菜单，重复调用即可刷新，
 *    调用方（如 router.afterEach）可放心高频触发，内部做了节流。
 */
export const hideShareMenus = async (): Promise<void> => {
  if (!isWxWorkEnv()) return;
  if (Date.now() - lastHideAt < HIDE_THROTTLE_INTERVAL) return;

  // 桥已就绪则立即隐藏；未就绪时等就绪事件补一次（覆盖首次冷启动）
  if (!hideMenusByBridge()) {
    document.addEventListener("WeixinJSBridgeReady", hideMenusByBridge, {
      once: true,
    });
  }

  // 签名链路依赖登录态（签名接口需携带 token），未登录时只保留上面的兜底；
  // 失败也不影响 Bridge 已完成的隐藏
  if (!getAuthToken()) return;
  try {
    await ensureSdkReady();
    await hideAllNonBaseMenuItem();
    await hideMenuItems({ menuList: HIDDEN_MENU_ITEMS });
    lastHideAt = Date.now();
  } catch (error) {
    console.warn("[wecomSdk] JS-SDK 隐藏右上角菜单失败：", error);
  }
};
